import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Sparkles, Upload, X, Loader2, Camera as CameraIcon, AlertTriangle, CheckCircle2,
  Info, RefreshCcw, Target, Image as ImageIcon, TrendingDown, ClipboardCheck,
} from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Line, Scatter, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { compressImage } from "../lib/imageCompress";
import { AIJawHistory } from "../components/AIJawHistory";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const VRSTE = ["jelen navadni", "srna", "divji prašič", "gams", "muflon", "damjak", "drugo/neznano"];
const RAZREDI = ["mladič", "lanščak", "odrasla", "starejša odrasla", "stara"];

/**
 * AI ocena starosti divjadi iz fotografije čeljusti.
 * Uporablja Gemini 3.1 Pro s priloženim referenčnim PDF-jem (Habermehl / Mitchell).
 */
const AIJawPage = () => {
  const { token, user } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };
  const isLeadership = ["predsednik", "gospodar", "administrator", "tajnik", "super_admin"].includes(user?.vloga);
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [result, setResult] = useState(null);
  const [secondOpinion, setSecondOpinion] = useState(null);
  const [secondLoading, setSecondLoading] = useState(false);
  const [accuracyStats, setAccuracyStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [access, setAccess] = useState({ loading: true, has_access: false, reason: null });
  const [pregRequest, setPregRequest] = useState({ has_pending: false, loading: false });

  const MAX_FILES = 3;

  const loadStats = () => {
    axios.get(`${API}/ai-jaw/accuracy-stats`, { headers: authHeaders })
      .then((rr) => setAccuracyStats(rr.data))
      .catch(() => {});
    axios.get(`${API}/ai-jaw/accuracy-timeline`, { headers: authHeaders })
      .then((rr) => setTimeline(rr.data.points || []))
      .catch(() => {});
  };

  useEffect(() => {
    // Access check first — if user has no rights, we render request gate
    axios.get(`${API}/ai-jaw/access-check`, { headers: authHeaders })
      .then((r) => {
        setAccess({ loading: false, has_access: !!r.data.has_access, reason: r.data.reason || null });
        if (r.data.has_access) {
          loadStats();
        } else if (r.data.reason === "role_not_allowed") {
          // Check if user already has pending preglednik request
          axios.get(`${API}/ai-jaw/preglednik-request/mine`, { headers: authHeaders })
            .then((rr) => setPregRequest({ has_pending: !!rr.data.has_pending, loading: false }))
            .catch(() => {});
        }
      })
      .catch(() => setAccess({ loading: false, has_access: false, reason: "error" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestPreglednikAccess = async () => {
    setPregRequest((p) => ({ ...p, loading: true }));
    try {
      const res = await axios.post(`${API}/ai-jaw/preglednik-request`, {}, { headers: authHeaders });
      setPregRequest({ has_pending: true, loading: false });
      toast.success(res.data?.message || "Zahtevek poslan vodstvu LD");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri pošiljanju zahtevka");
      setPregRequest((p) => ({ ...p, loading: false }));
    }
  };

  // Cleanup object URLs when previews change or component unmounts (prevents mobile OOM)
  useEffect(() => {
    return () => {
      previews.forEach((url) => { try { URL.revokeObjectURL(url); } catch (_e) { void _e; } });
    };
  }, [previews]);

  const onPickFiles = async (fileList) => {
    const arr = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length === 0) {
      toast.error("Izberite slikovno datoteko (JPEG/PNG/WEBP)");
      return;
    }
    // Compress each image client-side to avoid OOM on iPhone Safari with 12MP photos
    setPreparing(true);
    try {
      const compressed = [];
      for (const f of arr) {
        try {
          const out = await compressImage(f, { maxDim: 2000, quality: 0.82 });
          compressed.push(out);
        } catch (_) {
          compressed.push(f); // fallback to original if compress fails
        }
      }
      // Free previous preview URLs before creating new ones
      previews.forEach((url) => { try { URL.revokeObjectURL(url); } catch (_e) { void _e; } });
      const combined = [...files, ...compressed].slice(0, MAX_FILES);
      setFiles(combined);
      setPreviews(combined.map((f) => URL.createObjectURL(f)));
    } finally {
      setPreparing(false);
    }
  };

  const removeFile = (idx) => {
    // Revoke the removed preview URL immediately to free memory
    const removed = previews[idx];
    if (removed) { try { URL.revokeObjectURL(removed); } catch (_e) { void _e; } }
    const next = files.filter((_, i) => i !== idx);
    setFiles(next);
    setPreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  const analyze = async () => {
    if (files.length === 0) {
      toast.error("Priložite vsaj eno fotografijo");
      return;
    }
    setAnalyzing(true);
    setResult(null);
    setSecondOpinion(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await axios.post(`${API}/ai-jaw/analyze`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      const analysisId = r.data.analysis_id;
      if (r.data.status !== "processing") {
        setResult(r.data);
        return;
      }
      // Analiza teče v ozadju — pollamo status (5 AI klicev traja 1-3 min)
      const started = Date.now();
      const MAX_WAIT_MS = 6 * 60 * 1000;
      while (Date.now() - started < MAX_WAIT_MS) {
        await new Promise((res) => setTimeout(res, 3000));
        const s = await axios.get(`${API}/ai-jaw/analyze/${analysisId}`, { headers: authHeaders });
        if (s.data.status === "done" && s.data.result) {
          setResult({ analysis_id: analysisId, ...s.data.result });
          return;
        }
        if (s.data.status === "error") {
          toast.error(s.data.error || "Napaka pri AI analizi");
          return;
        }
      }
      toast.error("Analiza traja predolgo. Poskusite znova.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri analizi");
    } finally {
      setAnalyzing(false);
    }
  };

  const requestSecondOpinion = async () => {
    if (!result || files.length === 0) return;
    setSecondLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("previous_result", JSON.stringify({
        vrsta: result.vrsta,
        starostni_razred: result.starostni_razred,
        starost_let_min: result.starost_let_min,
        starost_let_max: result.starost_let_max,
        gotovost: result.gotovost,
      }));
      const r = await axios.post(`${API}/ai-jaw/second-opinion`, fd, {
        headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
      });
      setSecondOpinion(r.data);
      toast.success("Drugo mnenje pridobljeno");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri pridobivanju drugega mnenja");
    } finally {
      setSecondLoading(false);
    }
  };

  const resetAll = () => {
    // Revoke all preview URLs to free memory
    previews.forEach((url) => { try { URL.revokeObjectURL(url); } catch (_e) { void _e; } });
    setFiles([]);
    setPreviews([]);
    setResult(null);
    setSecondOpinion(null);
    if (inputRef.current) inputRef.current.value = "";
    if (cameraRef.current) cameraRef.current.value = "";
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="ai-jaw-page">
      <Card className="bg-gradient-to-br from-amber-800 to-amber-900 text-white border-0 shadow-lg">
        <CardContent className="p-4 sm:p-5 flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center">
            <Sparkles className="h-8 w-8" />
          </div>
          <div className="flex-1">
            <h1 className="text-lg sm:text-xl font-bold">AI ocena starosti divjadi</h1>
            <p className="text-xs sm:text-sm text-amber-100 mt-0.5">
              Slikaj spodnjo čeljust ali naloži do 3 fotografije za natančnejšo oceno.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Access gate: če uporabnik nima pravic, prikaži prošnjo za pravice preglednika namesto UI-ja */}
      {!access.loading && !access.has_access && access.reason === "role_not_allowed" && (
        <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid="ai-jaw-request-access-card">
          <CardContent className="p-4 sm:p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center flex-shrink-0">
                <Info className="h-5 w-5 text-amber-700 dark:text-amber-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 dark:text-amber-100">Za dostop potrebujete pravice preglednika</h3>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  AI ocena starosti je namenjena preglednikom divjačine in vodstvu LD. Če ste preglednik ali bi to postali, lahko zaprosite predsednika za pravice v enem kliku.
                </p>
              </div>
            </div>
            {pregRequest.has_pending ? (
              <div className="rounded-md bg-white dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800 p-3 flex items-center gap-2 text-sm text-amber-900 dark:text-amber-100" data-testid="preg-request-pending">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>Vaš zahtevek je bil poslan in čaka na obravnavo predsednika/gospodarja.</span>
              </div>
            ) : (
              <Button
                onClick={requestPreglednikAccess}
                disabled={pregRequest.loading}
                className="bg-amber-700 hover:bg-amber-800 text-white"
                data-testid="request-preglednik-access-btn"
              >
                {pregRequest.loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Pošiljam...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Zaprosi za pravice preglednika
                  </>
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Access gate: če LD nima vklopljene AI storitve */}
      {!access.loading && !access.has_access && access.reason === "not_enabled" && (
        <Card className="border-slate-300" data-testid="ai-jaw-not-enabled-card">
          <CardContent className="p-4 text-sm text-slate-700">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 text-slate-500" />
              Storitev AI ocena starosti ni vklopljena za vašo LD.
            </div>
            <p className="mt-1 text-slate-600">Kontaktirajte super-skrbnika za vklop.</p>
          </CardContent>
        </Card>
      )}

      {/* Če ni dostopa, ne renderiraj upload/analiznega UI-ja */}
      {(access.loading || !access.has_access) ? null : (
      <Tabs defaultValue="nova" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="nova" data-testid="tab-nova-analiza">Nova analiza</TabsTrigger>
        <TabsTrigger value="zgodovina" data-testid="tab-zgodovina">Zgodovina analiz</TabsTrigger>
      </TabsList>
      <TabsContent value="nova" className="space-y-4 mt-3">

      {/* Accuracy stats card - shown only when the LD has confirmed at least one analysis */}
      {accuracyStats && accuracyStats.total_confirmed > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/50" data-testid="ai-jaw-accuracy-card">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-emerald-700" />
              <h3 className="text-sm font-semibold text-emerald-900">Točnost AI (v vaši LD)</h3>
              <span className="text-[11px] text-slate-500 ml-auto">
                {accuracyStats.total_confirmed} potrjenih ocen komisije
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="bg-white rounded-lg p-2 border border-emerald-100">
                <div className="text-xs text-slate-500">Vrsta pravilna</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="acc-species">
                  {Math.round((accuracyStats.species_matches / accuracyStats.total_confirmed) * 100)}%
                </div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-emerald-100">
                <div className="text-xs text-slate-500">Razred pravilen</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="acc-class">
                  {Math.round((accuracyStats.class_matches / accuracyStats.total_confirmed) * 100)}%
                </div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-emerald-100">
                <div className="text-xs text-slate-500">± 1 leto</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="acc-within-1">
                  {Math.round((accuracyStats.within_1_year / accuracyStats.total_confirmed) * 100)}%
                </div>
              </div>
              <div className="bg-white rounded-lg p-2 border border-emerald-100">
                <div className="text-xs text-slate-500">Povp. odstopanje</div>
                <div className="text-lg font-bold text-emerald-700" data-testid="acc-avg-dev">
                  {accuracyStats.avg_deviation_years != null ? `${accuracyStats.avg_deviation_years} let` : "—"}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2 italic">
              Statistika temelji na primerjavi AI ocene s končno oceno komisije LD. Nižje odstopanje = boljša natančnost.
            </p>
            {timeline.length >= 2 && (
              <div className="mt-3 bg-white rounded-lg border border-emerald-100 p-2" data-testid="ai-jaw-learning-chart">
                <div className="flex items-center gap-1.5 mb-1 px-1">
                  <TrendingDown className="h-3.5 w-3.5 text-emerald-700" />
                  <span className="text-xs font-semibold text-emerald-900">Učenje AI skozi čas — odstopanje od ocene komisije (let)</span>
                </div>
                <ResponsiveContainer width="100%" height={160}>
                  <ComposedChart data={timeline} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="n" tick={{ fontSize: 10 }} label={{ value: "zaporedna potrditev", position: "insideBottom", offset: -2, fontSize: 9, fill: "#94a3b8" }} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={true} />
                    <RTooltip
                      formatter={(v, name) => [`${v} let`, name === "deviation" ? "Odstopanje" : "Drseče povprečje (5)"]}
                      labelFormatter={(l) => {
                        const p = timeline.find((t) => t.n === l);
                        return `#${l}${p?.vrsta ? ` · ${p.vrsta}` : ""}`;
                      }}
                      contentStyle={{ fontSize: 11 }}
                    />
                    <Scatter dataKey="deviation" fill="#f59e0b" name="deviation" />
                    <Line type="monotone" dataKey="rolling_avg" stroke="#059669" strokeWidth={2} dot={false} name="rolling_avg" />
                  </ComposedChart>
                </ResponsiveContainer>
                <p className="text-[10px] text-slate-500 px-1 italic">
                  Oranžne pike = odstopanje posamezne ocene. Zelena črta = drseče povprečje zadnjih 5 — padajoča črta pomeni, da se AI uči iz vaših potrditev.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Photo tips */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4 text-sm text-slate-700 space-y-1.5">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium mb-1">Nasveti za najboljšo natančnost:</p>
              <ul className="list-disc ml-5 space-y-0.5 text-xs sm:text-sm">
                <li>Fotografija naj bo <strong>ostro fokusirana</strong>, brez tresenja.</li>
                <li><strong>Stranski pogled</strong> spodnje čeljusti; naj bodo vidni vsi molarji (M1, M2, M3).</li>
                <li>Dobra osvetlitev (dnevna svetloba je najboljša).</li>
                <li>Priložite <strong>več fotografij</strong> (do 3) — različni koti izboljšajo oceno.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload area */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 sm:p-6">
            {/* Hidden inputs: separate for camera vs gallery so iOS/Android behavior is predictable */}
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
              data-testid="ai-jaw-camera-input"
            />
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
              data-testid="ai-jaw-file-input"
            />
            <div className="text-center mb-3">
              <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600">Priložite do {MAX_FILES} fotografije čeljusti</p>
              <p className="text-xs text-slate-400 mt-1">JPEG/PNG/WEBP · avtomatska optimizacija za mobitel</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="default"
                className="flex-1"
                onClick={() => cameraRef.current?.click()}
                disabled={preparing || files.length >= MAX_FILES}
                data-testid="ai-jaw-camera-btn"
              >
                <CameraIcon className="h-4 w-4 mr-2" />
                Fotografiraj
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => inputRef.current?.click()}
                disabled={preparing || files.length >= MAX_FILES}
                data-testid="ai-jaw-gallery-btn"
              >
                <ImageIcon className="h-4 w-4 mr-2" />
                Iz galerije
              </Button>
            </div>
            {preparing && (
              <p className="text-xs text-center text-slate-500 mt-2 flex items-center justify-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Pripravljam sliko...
              </p>
            )}
          </div>

          {previews.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {previews.map((url, idx) => (
                <div key={idx} className="relative aspect-square bg-slate-100 rounded-lg overflow-hidden" data-testid={`ai-jaw-preview-${idx}`}>
                  <img src={url} alt="preview" className="w-full h-full object-cover" />
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(idx); }}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                    data-testid={`ai-jaw-remove-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={analyze}
              disabled={analyzing || files.length === 0}
              className="flex-1"
              data-testid="ai-jaw-analyze-btn"
            >
              {analyzing ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analiza teče (do 30 s)…</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" />Analiziraj z AI</>
              )}
            </Button>
            {(files.length > 0 || result) && !analyzing && (
              <Button variant="outline" onClick={resetAll} data-testid="ai-jaw-reset">
                <RefreshCcw className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Result */}
      {result && <AIJawResult result={result} />}

      {/* Commission confirmation — feeds the AI feedback loop */}
      {result?.analysis_id && (
        <CommissionConfirm
          key={result.analysis_id}
          result={result}
          authHeaders={authHeaders}
          onConfirmed={loadStats}
        />
      )}

      {/* Second-opinion button + result */}
      {result && !secondOpinion && (
        <Button
          variant="outline"
          className="w-full"
          onClick={requestSecondOpinion}
          disabled={secondLoading}
          data-testid="ai-jaw-second-opinion-btn"
        >
          {secondLoading ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Pridobivam drugo mnenje…</>
          ) : (
            <><RefreshCcw className="h-4 w-4 mr-2" />🔄 Zahtevaj drugo mnenje (dodatna Pro analiza)</>
          )}
        </Button>
      )}
      {secondOpinion && (
        <div>
          <div className="text-sm font-semibold text-slate-700 mb-2 mt-2">🔍 Drugo mnenje AI:</div>
          <AIJawResult result={secondOpinion} isSecondOpinion={true} />
        </div>
      )}
      </TabsContent>
      <TabsContent value="zgodovina" className="mt-3">
        <AIJawHistory authHeaders={authHeaders} onStatsRefresh={loadStats} canDelete={isLeadership} />
      </TabsContent>
      </Tabs>
      )}
    </div>
  );
};

const CommissionConfirm = ({ result, authHeaders, onConfirmed }) => {
  const aiMid = ((Number(result.starost_let_min) || 0) + (Number(result.starost_let_max) || 0)) / 2;
  const [vrsta, setVrsta] = useState(VRSTE.includes(result.vrsta) ? result.vrsta : "drugo/neznano");
  const [razred, setRazred] = useState(RAZREDI.includes(result.starostni_razred) ? result.starostni_razred : "odrasla");
  const [starost, setStarost] = useState(aiMid ? String(aiMid) : "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const save = async () => {
    const age = parseFloat(String(starost).replace(",", "."));
    if (isNaN(age) || age < 0 || age > 30) {
      toast.error("Vnesite veljavno starost (0–30 let)");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/ai-jaw/confirm`, {
        analysis_id: result.analysis_id,
        confirmed: {
          vrsta,
          starostni_razred: razred,
          starost_let_min: age,
          starost_let_max: age,
        },
      }, { headers: authHeaders });
      setSaved(true);
      toast.success("Ocena komisije shranjena — AI se bo učil iz tega primera");
      if (onConfirmed) onConfirmed();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri shranjevanju");
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <Card className="border-emerald-300 bg-emerald-50/60" data-testid="commission-confirm-saved">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0" />
          <span>
            Ocena komisije shranjena: <strong>{vrsta}, {starost} let</strong>. AI bo ta primer upošteval pri prihodnjih analizah vaše LD.
          </span>
        </CardContent>
      </Card>
    );
  }

  const selectCls = "w-full h-9 rounded-md border border-slate-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <Card className="border-emerald-200" data-testid="commission-confirm-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-emerald-700" />
          <h3 className="text-sm font-semibold text-slate-800">Naša ocena (komisija LD)</h3>
        </div>
        <p className="text-xs text-slate-500">
          Vnesite starost, kot jo je ocenila vaša komisija. AI se iz vsake potrditve/popravka uči in postaja natančnejši za vašo LD.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Vrsta</label>
            <select value={vrsta} onChange={(e) => setVrsta(e.target.value)} className={selectCls} data-testid="commission-vrsta-select">
              {VRSTE.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Starost (let) — ocena komisije</label>
            <Input
              type="number"
              step="0.5"
              min="0"
              max="30"
              value={starost}
              onChange={(e) => setStarost(e.target.value)}
              placeholder="npr. 5"
              className="h-9"
              data-testid="commission-starost-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 block mb-1">Starostni razred</label>
            <select value={razred} onChange={(e) => setRazred(e.target.value)} className={selectCls} data-testid="commission-razred-select">
              {RAZREDI.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="commission-confirm-btn">
          {saving ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Shranjujem…</>
          ) : (
            <><CheckCircle2 className="h-4 w-4 mr-2" />Shrani oceno komisije (AI se uči)</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

const AIJawResult = ({ result, isSecondOpinion = false }) => {
  const confPct = Math.round((result.gotovost || 0) * 100);
  const accPct = Math.round((result.tocnost || 0) * 100);

  const confColor = confPct >= 75 ? "bg-emerald-500" : confPct >= 50 ? "bg-amber-500" : "bg-red-500";
  const accColor = accPct >= 75 ? "bg-emerald-500" : accPct >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <Card className={`border-2 ${isSecondOpinion ? "border-blue-300 bg-blue-50/30" : "border-primary/30"}`} data-testid={isSecondOpinion ? "ai-jaw-second-result" : "ai-jaw-result"}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <CheckCircle2 className={`h-5 w-5 ${isSecondOpinion ? "text-blue-600" : "text-primary"}`} />
          {isSecondOpinion ? "Drugo mnenje" : "Rezultat analize"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* AI reasoning (chain-of-thought) */}
        {result.razlogovanje && (
          <div className="bg-slate-50 border-l-4 border-primary rounded-r-lg p-3">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Razlogovanje AI</p>
            <p className="text-sm text-slate-700 italic" data-testid="ai-jaw-razlogovanje">{result.razlogovanje}</p>
          </div>
        )}

        {/* Species + age */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Vrsta</p>
            <p className="text-lg font-bold text-primary" data-testid="ai-jaw-vrsta">{result.vrsta || "—"}</p>
            {result.spol && result.spol !== "neznano" && (
              <p className="text-xs text-slate-600 mt-0.5">Spol: {result.spol}</p>
            )}
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 uppercase tracking-wide">Starost</p>
            <p className="text-lg font-bold text-primary" data-testid="ai-jaw-starost">
              {result.starost_let_min || result.starost_let_max
                ? `${result.starost_let_min}–${result.starost_let_max} let`
                : "—"}
            </p>
            <p className="text-xs text-slate-600 mt-0.5">Razred: {result.starostni_razred}</p>
          </div>
        </div>

        {/* Per-tooth wear scores (Habermehl method) */}
        {result.obraba_zob && Object.values(result.obraba_zob).some((v) => v !== null && v !== undefined) && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-emerald-900 uppercase mb-2">Obraba zob (Habermehl 0–5)</p>
            <div className="grid grid-cols-4 gap-2">
              {["M1", "M2", "M3", "P4"].map((key) => {
                const v = result.obraba_zob?.[key];
                return (
                  <div key={key} className="text-center">
                    <p className="text-[10px] text-slate-500">{key}</p>
                    <p className={`text-xl font-bold ${v === null || v === undefined ? "text-slate-300" : v >= 4 ? "text-red-600" : v >= 2 ? "text-amber-600" : "text-emerald-700"}`}>
                      {v === null || v === undefined ? "–" : v}
                    </p>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              0=svež zob, 5=ekstremna obraba. Referenca za lovca — ni podlaga za samodejni popravek starosti.
            </p>
          </div>
        )}

        {/* Confidence + Accuracy */}
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-600">Gotovost AI</span>
              <span className="font-semibold" data-testid="ai-jaw-gotovost">{confPct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${confColor} transition-all`} style={{ width: `${confPct}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-600">Ocena točnosti (na osnovi slike)</span>
              <span className="font-semibold" data-testid="ai-jaw-tocnost">{accPct}%</span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full ${accColor} transition-all`} style={{ width: `${accPct}%` }} />
            </div>
          </div>
          <p className="text-[11px] text-slate-500 italic">
            Gotovost = kako prepričan je AI. Točnost = koliko je ocena zanesljiva glede na kvaliteto slike.
          </p>
        </div>

        {/* Indicators */}
        {result.indikatorji?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-700 mb-1.5">Ključni indikatorji</p>
            <ul className="space-y-1" data-testid="ai-jaw-indikatorji">
              {result.indikatorji.map((ind, i) => (
                <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  <span>{ind}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Warnings */}
        {result.opozorila?.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-900 mb-1.5 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />
              Opozorila
            </p>
            <ul className="space-y-0.5" data-testid="ai-jaw-opozorila">
              {result.opozorila.map((o, i) => (
                <li key={i} className="text-sm text-amber-800">• {o}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Meta */}
        <div className="flex flex-wrap items-center justify-between gap-1.5 text-[11px] text-slate-400 pt-2 border-t">
          <span>
            Model: <Badge variant="outline" className="text-[10px] py-0 px-1.5">
              {result.model_used}
            </Badge>
            {result.feedback_loop_used && (
              <Badge variant="outline" className="ml-1.5 text-[10px] py-0 px-1.5 border-emerald-300 text-emerald-600" data-testid="ai-jaw-feedback-badge">
                Učenje iz potrditev vaše LD
              </Badge>
            )}
          </span>
          <span className="italic">Ocena AI — potrjuje komisija LD.</span>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIJawPage;
